import { useEffect } from "react";
import { Check, Compass, Footprints, Handshake } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import CircleForm from "@/components/circle/CircleForm";
import FieldStack from "@/components/circle/FieldNotes";
import { Eyebrow, Reveal } from "@/components/site/Bits";
import { captureUtm, DISCLAIMER } from "@/lib/circle";
import { track } from "@/lib/site";

const WHY = [
    [Compass, "Discover opportunities", "Selected land, development and real estate opportunities."],
    [Footprints, "Follow the journey", "How we discover, evaluate and develop land."],
    [Handshake, "Invest alongside us", "Participate in selected projects, subject to availability and eligibility."],
];

function Intro() {
    return (
        <div className="lg:col-span-7 lg:row-start-1">
            <Reveal><Eyebrow dark>The Investor Circle</Eyebrow></Reveal>
            <Reveal delay={80}><h1 className="font-serif text-[2.15rem] leading-[1.02] sm:text-5xl lg:text-6xl tracking-tight mt-4 md:mt-6 max-w-2xl" data-testid="ic-headline">Discover extraordinary <em className="italic text-sage">opportunities in Portugal.</em></h1></Reveal>
            <Reveal delay={160}><p className="hidden sm:block mt-6 text-base md:text-lg text-cream/80 leading-relaxed max-w-xl">Join a growing circle of people interested in investing alongside us. Discover selected opportunities, follow our projects, and see how we're building Verde 2050.</p></Reveal>
            <Reveal delay={220}><p className="mt-4 md:mt-5 inline-flex items-center gap-2 text-sm text-sage" data-testid="ic-free-note"><Check className="h-4 w-4" /> Free to join. No commitment.</p></Reveal>
        </div>
    );
}

function Benefits() {
    return (
        <ul className="lg:col-span-7 lg:row-start-2 grid sm:grid-cols-3 gap-5 sm:gap-6 border-t border-cream/15 pt-7" data-testid="ic-why">
            {WHY.map(([Icon, t, b], i) => (
                <Reveal as="li" key={t} delay={i * 80} className="flex sm:block gap-4">
                    <Icon className="h-5 w-5 text-sage shrink-0 mt-0.5" strokeWidth={1.4} />
                    <div>
                        <h2 className="sm:mt-4 font-serif text-xl text-cream leading-tight">{t}</h2>
                        <p className="mt-1.5 text-sm text-cream/65 leading-relaxed">{b}</p>
                    </div>
                </Reveal>
            ))}
        </ul>
    );
}

export default function InvestorCircle() {
    useEffect(() => { captureUtm(); track("ic_page_view"); }, []);
    return (
        <div data-testid="investor-circle-page" className="bg-forest text-cream min-h-screen">
            <SEO path="/investor-circle" og="investor-circle" title="The Investor Circle | Verde 2050"
                 description="Join The Investor Circle by Verde 2050. Discover selected land and real estate investment opportunities in Portugal and follow our journey from land to development." />
            <GroupHeader alwaysDark />
            <main>
                <section data-testid="ic-hero" className="pt-[5.5rem] md:pt-32 pb-14 md:pb-24 overflow-hidden">
                    <div className="max-w-7xl mx-auto px-5 md:px-12 grid lg:grid-cols-12 gap-x-12 xl:gap-x-16 gap-y-10 lg:gap-y-12 items-start">
                        <Intro />
                        <div id="join" className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-3">
                            <Reveal delay={120}><CircleForm /></Reveal>
                        </div>
                        <Benefits />
                        <div className="lg:col-span-7 lg:row-start-3"><FieldStack /></div>
                    </div>
                </section>
            </main>
            <p className="max-w-7xl mx-auto px-5 md:px-12 pb-10 text-xs text-cream/50 leading-relaxed" data-testid="ic-disclaimer">{DISCLAIMER}</p>
            <GroupFooter />
        </div>
    );
}
